<template>
  <article
    v-if="v2"
    class="quiz-card"
    :class="`quiz-card--${railTone}`"
    :aria-label="stepLabel || question"
  >
    <div class="quiz-card__rail" aria-hidden="true" />

    <div class="quiz-card__main">
      <div class="quiz-card__body">
        <div class="quiz-card__meta">
          <span class="quiz-card__chip">
            <svg
              class="quiz-card__chip-icon"
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M4.5 4.5a1.5 1.5 0 1 1 2.1 1.4c-.4.2-.6.5-.6.9M6 8.75h.005M11 6a5 5 0 1 1-10 0 5 5 0 0 1 10 0Z"
                stroke="currentColor"
                stroke-width="1"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            Quiz
          </span>
          <span v-if="stepLabel" class="quiz-card__step">{{ stepLabel }}</span>
        </div>

        <h3 class="quiz-card__question">{{ question }}</h3>

        <p v-if="!showLines && helperText" class="quiz-card__helper">{{ helperText }}</p>

        <div class="quiz-card__answers" role="group" :aria-label="question">
          <template v-if="showLines">
            <QuizAnswerLine
              v-for="answer in answers"
              :key="answer.id"
              :question="answer.text"
              :state="lineState(answer)"
            />
          </template>

          <template v-else>
            <QuizAnswerSelector
              v-for="answer in answers"
              :key="answer.id"
              :answer="answer.text"
              :is-selected="answer.isSelected"
              @select="emit('answer-selected', { answerId: answer.id, isSelected: $event })"
            />
          </template>
        </div>
      </div>

      <div class="quiz-card__footer" role="status">
        <span class="quiz-card__verdict" :class="`quiz-card__verdict--${verdict.tone}`">
          <svg
            class="quiz-card__verdict-icon"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            aria-hidden="true"
          >
            <circle cx="8" cy="8" r="6.5" stroke="currentColor" stroke-width="1.5" />
            <path
              v-if="verdict.tone === 'correct'"
              d="M5.2 8.2 7.1 10l3.7-3.9"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              v-else-if="verdict.tone === 'incorrect'"
              d="m6 6 4 4m0-4-4 4"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
            />
            <path
              v-else-if="verdict.tone === 'needs'"
              d="M8 5v3.4M8 10.6h.005"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
            />
          </svg>
          {{ verdict.label }}
        </span>
        <span v-if="verdict.detail" class="quiz-card__verdict-detail">{{ verdict.detail }}</span>
      </div>
    </div>
  </article>

  <article
    v-else
    class="quiz-question-card"
    :class="`quiz-question-card--${result || 'plain'}`"
  >
    <div v-if="result === 'incorrect'" class="quiz-question-card__alert" role="status">
      <span class="quiz-question-card__alert-icon">
        <img :src="errorIcon" alt="" class="quiz-question-card__alert-glyph" />
      </span>
      <p class="quiz-question-card__alert-text">{{ resultMessage || "Incorrect answer" }}</p>
    </div>

    <div class="quiz-question-card__block">
      <h3 class="quiz-question-card__question">{{ question }}</h3>

      <p
        v-if="visibleHelperText"
        class="quiz-question-card__helper"
        :class="{ 'quiz-question-card__helper--error': helperState === 'error' }"
      >
        {{ visibleHelperText }}
      </p>

      <div class="quiz-question-card__answers" role="group" :aria-label="question">
        <template v-if="readonly">
          <QuizAnswerLine
            v-for="answer in answers"
            :key="answer.id"
            :question="answer.text"
            :state="answer.isCorrect ? 'Correct' : 'Not Selected'"
          />
        </template>

        <template v-else>
          <QuizAnswerSelector
            v-for="answer in answers"
            :key="answer.id"
            :answer="answer.text"
            :is-selected="answer.isSelected"
            @select="emit('answer-selected', { answerId: answer.id, isSelected: $event })"
          />
        </template>
      </div>
    </div>
  </article>
</template>

<script setup>
import { computed } from "vue";
import QuizAnswerLine from "@components/QuizAnswerLine/QuizAnswerLine.vue";
import QuizAnswerSelector from "@components/QuizAnswerSelector/QuizAnswerSelector.vue";

const props = defineProps({
  question: { type: String, required: true },
  answers: { type: Array, default: () => [] },
  result: {
    type: String,
    default: "",
    validator: (value) => ["", "correct", "incorrect"].includes(value),
  },
  resultMessage: { type: String, default: "" },
  readonly: { type: Boolean, default: false },
  v2: { type: Boolean, default: false },
  index: { type: Number, default: null },
  total: { type: Number, default: 0 },
  helperState: {
    type: String,
    default: "default",
    validator: (value) => ["default", "error"].includes(value),
  },
  helperText: { type: String, default: "Questions can have multiple correct answers" },
  errorText: {
    type: String,
    default:
      "At least one answer needs to be selected. Questions can have multiple correct answers",
  },
});

const emit = defineEmits(["answer-selected"]);

const errorIcon = require("@components/assets/alert-circle-error.svg");

const visibleHelperText = computed(() => {
  if (props.readonly) return "";
  return props.helperState === "error" ? props.errorText : props.helperText;
});

const showLines = computed(() => props.readonly || props.result === "correct");

const railTone = computed(() => {
  if (props.result === "correct") return "green";
  if (props.result === "incorrect" || props.helperState === "error") return "red";
  return "grey";
});

const stepLabel = computed(() => {
  if (props.index === null) return "";
  return props.total > 0
    ? `Question ${props.index + 1} of ${props.total}`
    : `Question ${props.index + 1}`;
});

const verdict = computed(() => {
  if (props.result === "correct") return { tone: "correct", label: "Correct", detail: "" };
  if (props.result === "incorrect") {
    return { tone: "incorrect", label: "Incorrect", detail: props.resultMessage };
  }
  if (props.helperState === "error") {
    return {
      tone: "needs",
      label: "Needs an answer",
      detail: "At least one answer needs to be selected",
    };
  }
  return { tone: "pending", label: "Not yet answered", detail: "" };
});

function lineState(answer) {
  if (props.result !== "incorrect") return answer.isCorrect ? "Correct" : "Not Selected";
  if (answer.isSelected) return answer.isCorrect ? "Correct" : "Wrong";
  return answer.isCorrect ? "Expected" : "Not Selected";
}
</script>

<style scoped>
.quiz-question-card {
  display: flex;
  flex-direction: column;
  gap: 10px;
  box-sizing: border-box;
  color: #475467;
  font-family: Inter, sans-serif;
}

.quiz-question-card--plain {
  padding: 10px;
  border-radius: 4px;
}

.quiz-question-card--correct,
.quiz-question-card--incorrect {
  padding: 19px 13px;
  border: 1px solid #e4e4e4;
}

.quiz-question-card--correct {
  background: #dcfae6;
}

.quiz-question-card--incorrect {
  background: #fef3f2;
}

.quiz-question-card__alert {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 16px;
  border: 1px solid #d0d5dd;
  border-radius: 12px;
  background: #ffffff;
  box-shadow: 0 1px 1px rgba(16, 24, 40, 0.05);
}

.quiz-question-card__alert-icon {
  position: relative;
  flex: 0 0 auto;
  width: 20px;
  height: 20px;
  margin-left: 8px;
  color: #d92d20;
}

/* Concentric halo rings around the result icon */
.quiz-question-card__alert-icon::before,
.quiz-question-card__alert-icon::after {
  position: absolute;
  border: 2px solid currentColor;
  border-radius: 50%;
  content: "";
}

.quiz-question-card__alert-icon::before {
  inset: -4px;
  opacity: 0.3;
}

.quiz-question-card__alert-icon::after {
  inset: -9px;
  opacity: 0.1;
}

.quiz-question-card__alert-glyph {
  position: absolute;
  top: 8.33%;
  left: 8.33%;
  width: 83.34%;
  height: 83.34%;
}

.quiz-question-card__alert-text {
  flex: 1 1 auto;
  min-width: 0;
  margin: 0;
  color: #344054;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
}

.quiz-question-card__block {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 19px 13px;
  border: 1px solid #e4e4e4;
  background: #fbfbfb;
}

.quiz-question-card__question {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  line-height: 30px;
}

.quiz-question-card__helper {
  margin: 0;
  color: #7f56d9;
  font-size: 16px;
  font-weight: 700;
  line-height: 30px;
}

.quiz-question-card__helper--error {
  color: #f04438;
}

.quiz-question-card__answers {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.quiz-card {
  display: flex;
  flex-direction: row;
  align-items: stretch;
  box-sizing: border-box;
  width: 100%;
  overflow: hidden;
  border: 1px solid #eaecf0;
  border-radius: 8px;
  background: #ffffff;
  color: #475467;
  font-family: Inter, sans-serif;
}

.quiz-card__rail {
  flex: 0 0 4px;
  width: 4px;
  background-color: #98a2b3;
}

.quiz-card--red .quiz-card__rail {
  background-color: #f04438;
}

.quiz-card--green .quiz-card__rail {
  background-color: #17b26a;
}

.quiz-card__main {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-width: 0;
}

.quiz-card__body {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
}

.quiz-card__meta {
  display: flex;
  align-items: center;
  gap: 10px;
}

.quiz-card__chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 10px 2px 8px;
  border: 1px solid #eaecf0;
  border-radius: 12px;
  background: #f9fafb;
  color: #475467;
  font-size: 12px;
  font-weight: 500;
  line-height: 14.52px;
}

.quiz-card__step {
  color: #475467;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}

.quiz-card__question {
  margin: 0;
  color: #101828;
  font-size: 16px;
  font-weight: 700;
  line-height: 30px;
}

.quiz-card__helper {
  margin: 0;
  color: #7f56d9;
  font-size: 16px;
  font-weight: 700;
  line-height: 30px;
}

.quiz-card__answers {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.quiz-card__footer {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 12px;
  padding: 12px 16px;
  border-top: 1px solid #eaecf0;
  background: #f9fafb;
  font-size: 14px;
  line-height: 20px;
}

.quiz-card__verdict {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #475467;
  font-weight: 600;
}

.quiz-card__verdict--correct {
  color: #067647;
}

.quiz-card__verdict--incorrect,
.quiz-card__verdict--needs {
  color: #b42318;
}

.quiz-card__verdict-icon {
  flex: 0 0 auto;
}

.quiz-card__verdict-detail {
  color: #475467;
  font-weight: 400;
}
</style>
